import React, {useCallback, useMemo} from 'react';
import {Link} from 'react-router-dom';

import {CopyButton} from 'src/components/copy-button';
import {UserLink} from 'src/components/links';
import {USER_TABS} from 'src/constants';
import {ACCESS_RIGHTS, useAdminAccessRight} from 'src/hooks/access-rights';
import {useAccountManagerTypes} from 'src/hooks/dictionaries';
import {useEventTracking} from 'src/hooks/useEventTracking';

import {AvatarWrapped, Size} from '@pb/kit-component-avatar';
import {Dropdown} from '@pb/kit-component-dropdown';
import {Group, Item} from '@pb/kit-component-group';
import {Icon, Icons} from '@pb/kit-component-icon';

import {
    accountManagerErrorButton,
    accountManagerItem,
    accountManagerName,
    accountManagerPopoverEmail,
    accountManagerPopoverLink,
} from './account-manager.module.styl';

import type {AccountManagerInfo} from 'src/openapi/admin/models/AccountManagerInfo';


type Props = {
    accountManager: AccountManagerInfo,
};

export const AccountManager: React.FC<Props> = ({accountManager}) => {
    const {
        id,
        avatar,
        fullName,
        managerType,
        office,
        phone,
        email,
        primary,
        validationStatus,
    } = accountManager;
    const {accountManagerTypes} = useAccountManagerTypes();
    const {userProfileOpen} = useEventTracking();

    const {hasAccess} = useAdminAccessRight();

    const linkIsAvailable = useMemo(() => (
        hasAccess(ACCESS_RIGHTS.USER_SCREEN_VIEW)
    ), [hasAccess]);

    const accountManagerOpenTrack = useCallback(() => {
        userProfileOpen({
            place: 'Account Manager',
        });
    }, [userProfileOpen]);

    const content = (
        <div className="ellipsis">
            {primary ? <Icon type={Icons.STAR_FULL}/> : null} {fullName || email || ''}
        </div>
    );

    return (
        <Group alignItems="center">
            <AvatarWrapped
                src={avatar}
                alt={id.toString()}
                placeholder={Icons.USER}
                size={Size.MEDIUM}
                name={fullName || email}
            />
            {!!validationStatus.length && (
                <Dropdown
                    hoverEnabled={true}
                    anchor={({anchorRef, getAnchorProps}) => (
                        <span ref={anchorRef} {...getAnchorProps()}>
                            <Icon type={Icons.ERROR} className={accountManagerErrorButton}/>
                        </span>
                    )}>
                    <Group direction="v" gap="s" className="enable-selection" alignItems="start">
                        {validationStatus.map((error, i) => (
                            <Item key={i}>{error}</Item>
                        ))}
                    </Group>
                </Dropdown>
            )}
            <div style={{width: validationStatus.length ? 120 : 150}} className={accountManagerName}>
                <Dropdown
                    hoverEnabled={true}
                    placement="right"
                    horizontalOffset={70}
                    anchor={({anchorRef, getAnchorProps}) => (
                        <span ref={anchorRef} {...getAnchorProps()}>
                            {linkIsAvailable ? (
                                <Link to={`/profile/user/${id}/${USER_TABS.DETAILS.name}`} onClick={accountManagerOpenTrack}>
                                    {content}
                                </Link>
                            ) : content}
                        </span>
                    )}>
                    <Group direction="v" gap="s" className="enable-selection">
                        {fullName ? (
                            <Item className={accountManagerPopoverLink} grow={1}>
                                <UserLink userId={id} caption={fullName} place="Account Manager"/>
                            </Item>
                        ) : null}
                        {!!validationStatus.length && (
                            <Group direction="v" gap="s" className="enable-selection" alignItems="start">
                                {validationStatus.map((error, i) => (
                                    <Group key={i} alignItems="center">
                                        <Icon type={Icons.ERROR}/>
                                        <Item>{error}</Item>
                                    </Group>
                                ))}
                            </Group>
                        )}
                        <Group className={accountManagerItem}>
                            <b style={{width: 50}}>Office</b>
                            {office || '--'}
                        </Group>
                        <Group className={accountManagerItem}>
                            <b style={{width: 50}}>Phone</b>
                            {phone || '--'}
                        </Group>
                        <Group className={accountManagerPopoverEmail}>
                            <b style={{width: 50}}>Email</b>
                            {email || '--'}
                            {email ? <CopyButton copiedText={email} entityName="Email"/> : null}
                        </Group>
                    </Group>
                </Dropdown>
            </div>
            <Item>{accountManagerTypes?.find(({code}) => code === managerType)?.description || '--'}</Item>
        </Group>
    );
};
